import React, { useEffect, useState, useRef } from "react";
import { useStaticQuery, graphql } from "gatsby";
// import { usePreventScroll } from "@react-aria/overlays";
import useMobileDetection from "@klickinc/kfs-components-use-mobile-detection";
// import useScrollDirection from "@klickinc/kfs-components-use-scroll-direction";
import homeIcon from "@images/icons/home.svg";
import homeIconWhite from "@images/icons/home-white.svg";
import subnavAnchor from "@images/icons/subnav-anchor.svg";
import styled from "styled-components";
import SkipNav from "@components/SkipNav";
import Cta from "@components/CTA";
import Text from "@components/RTE/Text";
import { useLocation } from '@reach/router';
import ScrollToSection from "../ScrollToSection/ScrollToSection";
import logoGreen from "@images/logo-green.svg";
import logoWhite from "@images/logo-white.svg";
import { navigate } from "gatsby";

const HeaderContainer = styled.div`
	/* Glass background */
	position: relative;
	background: ${props => props.whiteHeader ?
		'linear-gradient(0deg, rgba(22, 95, 87, 0.15) 0%, rgba(22, 95, 87, 0.15) 100%), rgba(0, 0, 0, 0.35)' :
		'linear-gradient(0deg, rgba(211, 229, 234, 0.30) 0%, rgba(211, 229, 234, 0.30) 100%), rgba(255, 255, 255, 0.40)'
	};

	/* Glass Effects Light */
	box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.05) inset, 0px 20px 50px -20px rgba(0, 0, 0, 0.10);
	backdrop-filter: blur(20px);

	/* Create a pseudo-element for the gradient border that respects border-radius */
	&::before {
		content: "";
		position: absolute;
		top: -1px;
		left: -1px;
		right: -1px;
		bottom: -1px;
		border: 1px solid transparent;
		background: linear-gradient(103deg, rgba(255, 255, 255, 0.70) 7.86%, rgba(92, 143, 137, 0.60) 12.11%, rgba(92, 143, 137, 0.60) 88.58%, rgba(255, 255, 255, 0.70) 92.83%) border-box;
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 1;
	}

	@media (max-width: 1023px) {
		nav > ul > li:last-child a{
			border:0;
			text-decoration: underline;
		}

		nav > ul > li:nth-last-child(2) a {
			border: 0;
			padding-bottom: 0;
			text-decoration: underline;
		}
	}
`;

const HeaderWrapper = styled.div`
	position: fixed;
	width: 100%;
	left: 50%;
	max-width: 1296px;
	margin:0;
	transition: transform 1000ms;
	transform: translate(-50%, 0);

`;

const Header = ({ innerRef, whiteHeader }) => {
	const data = useStaticQuery(graphql`
		query Header {
			contentfulContainer(contentful_id: {eq: "7yKw9FdzzBel9pF1y6Wt7A"}) {
				...HeaderFragment
			}
		}
	`);

	const [isMobile, setIsMobile] = useState(false);
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const [activeSubNav, setActiveSubNav] = useState(null);
	const [isScrolled, setIsScrolled] = useState(false);
	const [headerHeight, setHeaderHeight] = useState(0);
	const headerRef = useRef(null);
	const mobileDetection = useMobileDetection();
	const [currentPath, setCurrentPath] = useState('');

	// Custom scroll direction implementation
	const [scrollDir, setScrollDir] = useState('up');
	const [lastScrollY, setLastScrollY] = useState(0);

  	const location = useLocation();
	useEffect(() => {
		// Handle scroll prevention for iOS and other devices
		if (mobileNavOpen) {
			document.body.style.overflow = 'hidden';
			document.body.style.position = 'fixed';
			document.body.style.width = '100%';
			document.body.style.top = `-${window.scrollY}px`;
		} else {
			const scrollY = document.body.style.top;
			document.body.style.overflow = '';
			document.body.style.position = '';
			document.body.style.width = '';
			document.body.style.top = '';
			if (scrollY) {
				window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
			}
		}
	}, [mobileNavOpen]);


	useEffect(() => {
		// Get header height once the component is mounted
		if (headerRef.current) {
			setHeaderHeight(headerRef?.current?.getBoundingClientRect()?.height);
		}
	}, []);

	useEffect(() => {
		setIsMobile(mobileDetection);
	}, [mobileDetection]);

	useEffect(() => {
		if (!isMobile) {
			setMobileNavOpen(false);
			setActiveSubNav(null);
		}
	}, [isMobile]);

	// Add scroll detection
	useEffect(() => {
		const handleScroll = () => {
			const scrollPosition = window.scrollY;
			setIsScrolled(scrollPosition > 30);

			// Determine scroll direction
			if (scrollPosition > lastScrollY) {
				setTimeout(() => {
					setScrollDir('down');
				}, 150);
			} else if (scrollPosition < lastScrollY) {
				setTimeout(() => {
					setScrollDir('up');
				}, 150);
			}
			setLastScrollY(scrollPosition);
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, [headerHeight, lastScrollY]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			setCurrentPath(window.location.pathname);
		}
		console.debug(mainContainer?.backgroundImage?.file?.url)

	}, []);

	const toggleMobileMenu = (e) => {
		e.preventDefault();
		if (!isMobile) return false;
		setMobileNavOpen(!mobileNavOpen);
		setActiveSubNav(null);
	};

	// Extract data from the two main containers
	const [utilityContainer, mainContainer] = data?.contentfulContainer?.items || [];

	// Get disclaimer text from the first container
	const disclaimerText = utilityContainer?.items?.[0]?.text?.raw;

	// Get utility links from the first container
	const utilityLinks = utilityContainer?.items?.[1]?.items || [];

	// Get navigation items and logo from the second container
	const navItems = mainContainer?.items || [];

	// Handle section navigation click
	const handleMobileNavClose = () => {
		if (mobileNavOpen) {
			setMobileNavOpen(false);
			setActiveSubNav(null);
		}
	};

	const submeOpen = (label, e, location) => {
		if (isMobile) {
			e.preventDefault();
			setActiveSubNav(activeSubNav === label ? null : label);
			const target = e.target;
			target.querySelector('img')?.classList.toggle('group-hover:rotate-180', target.classList.toggle('subitem'));
		} else {
			if (location !== '#' && location !== null && location !== undefined) {
				window.location.href = location;
			} else {
				e.preventDefault();
			}
		}
	}

	return (
		<header
			ref={headerRef}
			className={`absolute z-[60]  w-full px-4  transition-all bg-gradient-header`}
		>
			<SkipNav
				linkToMain="#main-content"
				className="block h-0 bg-white p-0 text-center uppercase opacity-0 focus:h-auto focus:py-2 focus:opacity-100 lg:text-lg"
			>
				Skip to main content
			</SkipNav>

			<div className="hidden lg:block lg:fixed w-full max-w-default_plus mx-auto h-0 left-[50%] -translate-x-1/2 z-[100]">
				<Cta
					displayType="Link"
					href="/connect"
					label="Connect"
					target="Same"
          className=" sectionNav-connect-gradient"
					style={` absolute right-4 xl:right-0 top-0 text-p_medium ${whiteHeader ? 'text-white' : 'text-dark_green'}
!font-bold rounded-b-[32px] px-[32px] py-[16px] bg-[#7AC5934D]  w-[162px]  text-center ${isScrolled ? 'opacity-100' : ''} transition-opacity duration-300`}
				/>
			</div>
			<HeaderWrapper
				className={`px-4 xl:px-0 header-wrapper ${
					mobileNavOpen ? (whiteHeader ? 'bg-dark_green h-[100dvh]' : 'bg-white h-[100dvh]') : ''} `}
				style={{
					transform: isScrolled
						? (scrollDir === 'up'
							? 'translate(-50%, 0px)'
							: 'translate(-50%, -160px)')
						: "",
					position: isScrolled ? (scrollDir === 'up'
						? 'fixed'
						: 'fixed')
						: "",
				}}
			>
				{/*Background shadow*/}
				<div className={`${whiteHeader ? 'bg-header-dark-gradient' : 'bg-header-white-gradient'} absolute top-0 left-1/2 -translate-x-1/2 !w-[100vw] h-full -z-[1]`}></div>
				{/* Disclaimer and Utility Links */}
				<div className=" flex  md:justify-between border-separator mb-4 gtm-header">
					{disclaimerText && (
						<Text
							content={disclaimerText}
							style={`[&>p]:text-p_10 [&>p]:!font-[500] md:[&>p]:text-p_medium ${whiteHeader ? 'text-white' : 'text-dark_green'} md:pl-10 mt-4 mobileMax:relative mobileMax:[&:after]:content-[''] mobileMax:[&:after]:absolute mobileMax:[&:after]:right-0 mobileMax:[&:after]:top-0 mobileMax:[&:after]:w-[1px] mobileMax:[&:after]:h-[20px] mobileMax:[&:after]:bg-[#6494A1] mobileMax:[&:after]:mb-[10px] mobileMax:pr-[5px] mobileMax:mr-[10px]`}
						/>
					)}
					<div className="flex justify-end gap-6">
						{utilityLinks.map((link) => (
							<Cta
								key={link.id}
								displayType={link.displayType || "Link"}
								href={link.href}
								label={link.label}
								target={link.target}
								style={`${whiteHeader ? 'text-white' : 'text-dark_green'} underline
									${link.label === "Connect" ? "no-underline lg:invisible !font-bold rounded-b-[32px] px-[24px] md:px-[32px] py-[12px] md:py-[16px] bg-[#7AC5934D]  w-[108px] md:w-[162px] text-center" : "!text-p_10 !font-[500] md:!text-p_medium mt-4"}
									${link.label.toLowerCase().includes("patient site") ? "hidden lg:block" : ""}
									${link.label.toLowerCase().includes("surgeon selector") ? "hidden lg:block" : ""}
									`}
							/>
						))}
					</div>
				</div>

				{/* Main Navigation */}
				<HeaderContainer className={`gtm-nav mx-auto flex flex-row items-center justify-between lg:px-6 py-4 lg:py-5 xl:px-12 rounded-[32px] xl:rounded-[100px] [&:before]:!rounded-[32px] lg:[&:before]:!rounded-[100px]
				${mobileNavOpen ? "grid grid-cols-2" : ""}
				${whiteHeader ? 'bg-nav-gradient-green' : ''}
				`} whiteHeader={whiteHeader}>

					<Cta
						displayType="Link"
						href="/"
						label={<img src={whiteHeader ? logoWhite : logoGreen} alt="EXPAREL Logo" />}
						style={` w-[144px] xl:w-[auto] ml-[24px] lg:ml-0`}
						target="Same"
					/>

					{isMobile && (
						<button
							onClick={toggleMobileMenu}
							aria-expanded={mobileNavOpen}
							aria-haspopup="menu"
							aria-label="toggle mobile menu"
							className={`h-10 w-10 self-start mr-[20px] lg:mr-0  lg:hidden ${mobileNavOpen ? "ml-auto" : ""}`}
						>
							{/* {mobileNavOpen ? "\u2715" : "\u2630"} */}
							{!mobileNavOpen &&
								<svg className="menu__toggle__icon" height="26" width="28" data-name="menu__toggle__icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 32">
									<title>Menu Toggle Icon</title>
									<path d="M2 5.9502H26" stroke={whiteHeader ? "#fff" : "#165F57"} stroke-width="3" stroke-linecap="round" />
									<path d="M2 14.5752H26" stroke={whiteHeader ? "#fff" : "#165F57"} stroke-width="3" stroke-linecap="round" />
									<path d="M2 23.2002H26" stroke={whiteHeader ? "#fff" : "#165F57"} stroke-width="3" stroke-linecap="round" />
								</svg>
							}
							{mobileNavOpen &&
								<svg className="menu__toggle__icon" height="26" width="28" data-name="menu__toggle__icon" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 32">
									<title>Menu Toggle Icon</title>
									<path d="M5.51465 5.53448L22.4852 22.505" stroke={whiteHeader ? "#fff" : "#165F57"} stroke-width="3" stroke-linecap="round" />
									<path d="M5.51465 22.5051L22.4852 5.5345" stroke={whiteHeader ? "#fff" : "#165F57"} stroke-width="3" stroke-linecap="round" />
								</svg>
							}
						</button>
					)}

					<nav
						aria-label="main"
						className={`lg:flex lg:grow lg:justify-center md:max-w-[785px] xl:max-w-[930px] ${mobileNavOpen ? "col-span-2 block" : "hidden"
							}`}
					>
						<ul className={`block flex-col md:flex lg:flex-row justify-space-between md:gap-4 xl:gap-8 items-stretch lg:items-center flex-1 ${mobileNavOpen ? "block max-h-[calc(100dvh-200px)] overflow-y-auto" : ""
							}`}>
							{navItems.map((item, index) => (
								<li key={item.id || item.label} className={`px-[20px] lg:px-0 group relative text-center hover:cursor-pointer h-[100%] block lg:flex items-center justify-center ${index >= navItems.length - 2 ? "lg:hidden" : ""} ${item.href === "/" ? "min-w-[24px]" : ""} ${(currentPath === item.href || currentPath === item.navLink?.href || (currentPath.includes("efficacy") && (item.href?.includes("efficacy") || item.navLink?.href?.includes("efficacy")))) && "[&>a]:!font-[700] [&:before]:content-[''] [&:before]:absolute [&:before]:top-[18px] [&:before]:left-[-16px] [&:before]:rotate-90 lg:[&:before]:top-auto lg:[&:before]:bottom-[-23px] xl:[&:before]:bottom-[-28px] lg:[&:before]:left-1/2 lg:[&:before]:rotate-0 [&:before]:w-[46px] [&:before]:h-[20px] [&:before]:bg-[url('@images/icons/fire.svg')] [&:before]:bg-no-repeat [&:before]:bg-center"
									} ${currentPath === "/" ? "lg:[&:before]:hidden xl:[&:before]:block lg:[&:before]:bottom-[5px] xl:[&:before]:bottom-[5px] lg:[&:before]:w-[15px] xl:[&:before]:w-[15px] lg:[&:before]:-translate-x-[50%] xl:[&:before]:-translate-x-[50%]" : ""
									} ${item.href === '/' ? "hover:[&:before]:content-[''] hover:[&:before]:absolute hover:[&:before]:top-[18px] hover:[&:before]:left-[-16px] hover:[&:before]:rotate-90 hover:[&:before]:w-[46px] hover:[&:before]:h-[20px] hover:[&:before]:bg-[url('@images/icons/fire.svg')] hover:[&:before]:bg-no-repeat hover:[&:before]:bg-center lg:hover:[&:before]:top-auto lg:hover:[&:before]:bottom-[5px] lg:hover:[&:before]:left-1/2 lg:hover:[&:before]:-translate-x-[50%] lg:hover:[&:before]:rotate-0 lg:hover:[&:before]:w-[15px] lg:hover:[&:before]:hidden xl:hover:[&:before]:block xl:hover:[&:before]:bottom-[5px] xl:hover:[&:before]:w-[15px] xl:hover:[&:before]:-translate-x-[50%]" : ""}
									${item.href !== "/" && item.subNav ? "subitem lg:[&:before]:-translate-x-[69%]" : "lg:[&:before]:-translate-x-[50%]"}`}>
									{item.href !== "/" ? (item.subNav ? (
										<>
											<Cta
												displayType={item.navLink?.displayType || "Link"}
												label={item.navLink?.label}
												style={`border-b-2 border-soft_blue lg:border-none text-left lg:text-center py-[20px] w-full relative pr-6 !decoration-none font-gt_walsheim lg:py-0 text-p ${whiteHeader ? 'text-white' : 'text-dark_green'} inline-block transition-all duration-100 hover:text-emerald-700`}
												target={item.navLink?.target}
												href={item.navLink?.href}
												onClick={(e) => {
													submeOpen(item.navLink?.label, e, item.navLink?.href);
												}}
											>
												<img
													src={subnavAnchor}
													alt=""
													className={` absolute right-[2px] top-1/2 -translate-y-1/2 transition-transform duration-100  ease-in-out group-hover:rotate-180`}
												/>
											</Cta>
											<div className={`${isMobile ? (activeSubNav === item.navLink?.label ? 'block' : 'hidden') : ''} lg:invisible group-hover:visible relative lg:absolute lg:left-[calc(50%-113px)] lg:top-[calc(100%+12px)] xl:top-[calc(100%+17px)] transform transition-all duration-300 ease-in-out lg:opacity-0 lg:group-hover:opacity-100 z-20 lg:min-w-[200px] translate-y-2 lg:backdrop-blur-[20px] `}>
												<ul className={`mobileTabletMax:flex mobileTabletMax:items-start lg:bg-white py-[10px] px-[24px]  lg:p-[24px] lg:rounded-b-[32px] flex flex-col gap-[10px] lg:gap-[24px] lg:backdrop-blur-[20px] lg:shadow-[0px_6px_23px_-5px_rgba(0,0,0,0.4)]`}>
													{item.subNav.map((subItem) => (
														<li key={subItem.id} className={`${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-100 ease-in-out`} style={{ transitionDelay: `${isMobile ? '0ms' : (70 * (item.subNav.indexOf(subItem) + 1))}ms` }}>
															{subItem.href?.startsWith('#') ? (
																<ScrollToSection
																	sectionId={subItem.href.substring(1)}
																	headerRef={innerRef}
																	onClickCallback={handleMobileNavClose}
																	ctaProps={{
																		displayType: subItem.displayType || "Link",
																		target: subItem.target,
																	}}
																	className={`py-[10px] !decoration-none leading-[normal] font-gt_walsheim lg:py-0 text-p ${whiteHeader ? 'text-white lg:text-dark_green' : 'text-dark_green'} inline-block transition-all duration-100 hover:text-teal-700`}
																>
																	{subItem.label}
																</ScrollToSection>
															) : (
																	<Cta
																		displayType={subItem.displayType || "Link"}
																		href={subItem.href}
																		label={subItem.label}
																		onClick={(e) => {
																			e.preventDefault();
																			navigate(subItem.href);
																			handleMobileNavClose();
																		}}
																		target={subItem.target}
																		style={`py-[10px] !decoration-none leading-[normal] font-gt_walsheim lg:py-0 text-p ${whiteHeader ? 'text-white lg:text-dark_green' : 'text-dark_green'} inline-block transition-all duration-100 hover:text-teal-700`}
																	/>
															)}
														</li>
													))}
												</ul>
											</div>
										</>
									) : (
										<Cta
											displayType={item.displayType || "Link"}
											href={item.href || item.navLink?.href}
											label={item.label}
											target={item.target || item.navLink?.target}
												style={`border-b-2 border-soft_blue lg:border-none text-left lg:text-center w-full lg:w-max_content py-[20px] !decoration-none  leading-[normal] font-gt_walsheim lg:py-0 text-p ${whiteHeader ? 'text-white' : 'text-dark_green'} inline-block ${location.pathname === '/dosing-and-administration' ? "xl:max-w-[100px]" : ""}`}
										/>
									)) : (
											<a href={item.href || item.navLink?.href} className={`border-b-2 border-soft_blue lg:border-none text-left lg:text-center py-[20px] w-full relative !decoration-none font-gt_walsheim lg:py-0 text-p ${whiteHeader ? 'text-white' : 'text-dark_green'} inline-block transition-all duration-100 hover:text-emerald-700 flex items-center lg:justify-center mobileMax:mt-[20px]`}>
												<img src={whiteHeader ? homeIconWhite : homeIcon} alt="Home" className="hidden lg:block h-[22px] w-[22px] lg:h-[24px] lg:w-[24px] leading-[normal]" />
												<span className="lg:hidden">Home</span>
											</a>
									)}
								</li>
							))}
						</ul>
					</nav>
				</HeaderContainer>
			</HeaderWrapper>
		</header>
	);
};

export default Header;